import { Plus, Tag } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { SearchInput } from '../../../../components/Input';
import { MenuGroup, MenuItem } from '../../../../components/Popover';
import { useTagOptions } from '../../api/candidate-browse-api';
import styles from './QuickActions.module.css';

/** A picked tag: an existing one by id, or a new name the server creates (or reuses, any case). */
export interface TagChoice {
  readonly tagId?: string;
  readonly name: string;
}

const SHOWN_TAGS = 8;

/** The small picker beside a Tag button: the existing tags, or type a new one and press Enter. */
export function TagPicker({ alreadyHas = [], onPick }: { readonly alreadyHas?: readonly string[]; readonly onPick: (choice: TagChoice) => void }): ReactNode {
  const tags = useTagOptions();
  const [query, setQuery] = useState('');
  const typed = query.trim();
  const lower = typed.toLowerCase();
  const offered = (tags.data ?? []).filter((tag) => !alreadyHas.includes(tag.id) && tag.name.toLowerCase().includes(lower));
  const exact = (tags.data ?? []).find((tag) => tag.name.toLowerCase() === lower);
  const pickTyped = (): void => {
    if (typed === '') return;
    onPick(exact ? { tagId: exact.id, name: exact.name } : { name: typed });
  };

  return (
    <div className={styles.picker}>
      <SearchInput
        aria-label="Find or add a tag"
        placeholder="Find or add a tag"
        value={query}
        maxLength={40}
        autoFocus
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          pickTyped();
        }}
      />
      <MenuGroup>
        {offered.slice(0, SHOWN_TAGS).map((tag) => (
          <MenuItem key={tag.id} icon={<Tag size={14} />} onClick={() => onPick({ tagId: tag.id, name: tag.name })}>
            {tag.name}
          </MenuItem>
        ))}
        {typed !== '' && exact === undefined && (
          <MenuItem icon={<Plus size={14} />} onClick={pickTyped}>
            Add “{typed}”
          </MenuItem>
        )}
        {offered.length === 0 && typed === '' && <p className={styles.none}>{tags.isPending ? 'Loading…' : 'No tags yet — type one.'}</p>}
      </MenuGroup>
    </div>
  );
}
